import React, { useMemo, useState } from "react";
import { Send, Loader2, FileText, CheckCircle2, AlertTriangle, Landmark, Mail, Printer } from "lucide-react";

// Reusable "Submit to Lender for Approval" panel for borrower + broker portals.
// base = "borrower/applications" | "broker/clients"
const CHANNELS = [
  { v: "efax", label: "eFax", icon: Send },
  { v: "email", label: "Email", icon: Mail },
  { v: "mail", label: "USPS Mail", icon: Printer },
];

export default function SubmitToLender({ app, authAxios, base, onChanged, readOnly = false }) {
  const [open, setOpen] = useState(false);
  const [channel, setChannel] = useState(app?.lender_submission?.channel || "efax");
  const [fax, setFax] = useState(app?.lender_submission?.lender_fax || "");
  const [email, setEmail] = useState(app?.lender_submission?.lender_email || "");
  const [address, setAddress] = useState(app?.lender_submission?.lender_address || app?.current_lender_address || "");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [missing, setMissing] = useState([]);

  const status = app?.status;
  const alreadySubmitted = ["submitted_to_lender", "lender_approved", "lender_declined"].includes(status);

  const localMissing = useMemo(() => {
    const m = [];
    if (!(app.borrower_full_name || app.borrower_name || "").trim()) m.push("Borrower full legal name");
    if (!(app.borrower_address || "").trim()) m.push("Borrower mailing address");
    if (!(app.current_lender || "").trim()) m.push("Current lender / servicer name");
    if (!(app.account_number || "").trim()) m.push("Loan / account number");
    if (!(Number(app.current_balance) > 0)) m.push("Current balance / payoff amount");
    if (["mortgage", "auto"].includes(app.debt_type) && !(app.property_or_collateral || "").trim()) m.push("Property / collateral description");
    return m;
  }, [app]);

  const channelReady =
    channel === "efax" ? !!fax.trim() :
    channel === "email" ? !!email.trim() :
    !!address.trim();

  const previewPackage = async () => {
    try {
      const r = await authAxios.get(`/${base}/${app.id}/lender-package.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank");
    } catch {
      setMsg({ ok: false, t: "Could not open the package preview." });
    }
  };

  const submit = async () => {
    setBusy(true); setMsg(null); setMissing([]);
    try {
      const r = await authAxios.post(`/${base}/${app.id}/submit-to-lender`, {
        channel, lender_fax: fax, lender_email: email, lender_address: address,
      });
      const chan = CHANNELS.find((c) => c.v === channel)?.label || channel;
      setMsg({ ok: true, t: channel === "mail" ? "Print-and-mail packet prepared." : `Submitted to lender via ${chan}.` });
      setOpen(false);
      onChanged && onChanged();
      return r;
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d && typeof d === "object" && Array.isArray(d.missing)) {
        setMissing(d.missing);
        setMsg({ ok: false, t: "Please complete the required fields before submitting." });
      } else {
        setMsg({ ok: false, t: (typeof d === "string" ? d : "Submission failed.") });
      }
    } finally {
      setBusy(false);
    }
  };

  if (alreadySubmitted) {
    const sub = app.lender_submission || {};
    const needsMail = !!sub.needs_print_mail || (sub.delivery || {}).status === "needs_contact";
    const chan = CHANNELS.find((c) => c.v === sub.channel)?.label || "eFax";
    const dstatus = (sub.delivery || {}).status || sub.fax_status;
    const label = status === "lender_approved" ? "Lender Approved" : status === "lender_declined" ? "Lender Declined" : needsMail ? "Queued — Print & Mail to Lender" : "Sent to Lender — Pending Approval";
    const cls = status === "lender_approved" ? "text-[#3DBB8E] border-[#0B6B4F]/50 bg-[#0B6B4F]/12" : status === "lender_declined" ? "text-[#FF8585] border-[#9E2A2B]/50 bg-[#9E2A2B]/12" : needsMail ? "text-[#E4B84A] border-[#E4B84A]/45 bg-[#E4B84A]/10" : "text-[#E4C97A] border-[#B8860B]/45 bg-[#B8860B]/10";
    const dest = sub.channel === "email" ? sub.lender_email : sub.channel === "mail" ? sub.lender_address : sub.lender_fax;
    return (
      <div className={`mt-4 rounded-md border px-3.5 py-2.5 ${cls}`} data-testid={`lender-status-${app.id}`}>
        <div className="flex items-center gap-2 text-[12px] font-semibold">
          <CheckCircle2 size={14} /> {label}
        </div>
        <div className="text-[11px] opacity-90 mt-1">
          {needsMail
            ? "No lender fax/email was found — your approval package is prepared and will be printed & mailed to your current lender/servicer by our team."
            : <>Delivered via {chan}{dest ? ` to ${dest}` : ""}{sub.submitted_at ? ` on ${String(sub.submitted_at).slice(0, 10)}` : ""}{dstatus ? ` · status: ${dstatus}` : ""}.</>}
        </div>
      </div>
    );
  }

  if (readOnly) {
    return (
      <div className="mt-4 rounded-md border border-[#B8860B]/45 bg-[#B8860B]/10 px-3.5 py-2.5 text-[#E4C97A]" data-testid={`lender-status-${app.id}`}>
        <div className="flex items-center gap-2 text-[12px] font-semibold"><Send size={14} /> Preparing your lender package</div>
        <div className="text-[11px] opacity-90 mt-1">Your payoff request, authorization &amp; agreement are being transmitted to your current lender/servicer.</div>
      </div>
    );
  }

  return (
    <div className="mt-4 rounded-md border border-[#2A2A22] bg-[#0F0F0A] p-3.5" data-testid={`submit-lender-${app.id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <Landmark size={14} className="text-[#B8860B]" />
        <span className="text-[12px] font-semibold text-[#E4C97A]">Submit to Lender for Approval</span>
        <span className="text-[10.5px] text-[#8A8A8A]">— send the payoff request, authorization &amp; agreement</span>
        <button onClick={() => setOpen((o) => !o)} data-testid={`submit-lender-toggle-${app.id}`}
          className="ml-auto text-[10.5px] uppercase tracking-[0.1em] font-bold text-[#B8860B] hover:text-[#E4C97A]">
          {open ? "Close" : "Start"}
        </button>
      </div>

      {open && (
        <div className="mt-3 space-y-2.5">
          {localMissing.length > 0 && (
            <div className="rounded-md border border-[#9E2A2B]/40 bg-[#9E2A2B]/10 px-3 py-2" data-testid={`submit-lender-missing-${app.id}`}>
              <div className="flex items-center gap-1.5 text-[11px] font-semibold text-[#FF8585]"><AlertTriangle size={12} /> Complete these first:</div>
              <ul className="list-disc pl-5 mt-1 text-[11px] text-[#FFC9C9]">
                {localMissing.map((m, i) => <li key={i}>{m}</li>)}
              </ul>
            </div>
          )}

          <div>
            <label className="block text-[10px] uppercase tracking-[0.12em] text-[#8A8A8A] mb-1">Delivery channel</label>
            <div className="flex gap-2" data-testid={`submit-lender-channels-${app.id}`}>
              {CHANNELS.map((c) => {
                const Icon = c.icon;
                const active = channel === c.v;
                return (
                  <button key={c.v} onClick={() => setChannel(c.v)} data-testid={`submit-lender-channel-${c.v}-${app.id}`}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] uppercase tracking-[0.1em] font-bold rounded-sm border ${active ? "bg-[#B8860B] text-[#0A0A0A] border-[#B8860B]" : "bg-[#0A0A0A] text-[#9A9A90] border-[#2A2A22] hover:border-[#3A3A3A]"}`}>
                    <Icon size={12} /> {c.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            {channel === "efax" && (
              <div>
                <label className="block text-[10px] uppercase tracking-[0.12em] text-[#8A8A8A] mb-1">Lender / servicer fax *</label>
                <input value={fax} onChange={(e) => setFax(e.target.value)} placeholder="+13125551234" data-testid={`submit-lender-fax-${app.id}`}
                  className="w-full bg-[#0A0A0A] border border-[#2A2A22] rounded-sm px-3 py-2 text-[12.5px] text-[#ECECE4] focus:outline-none focus:border-[#B8860B]" />
              </div>
            )}
            {channel === "email" && (
              <div>
                <label className="block text-[10px] uppercase tracking-[0.12em] text-[#8A8A8A] mb-1">Lender email *</label>
                <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="payoffs@lender.com" data-testid={`submit-lender-email-${app.id}`}
                  className="w-full bg-[#0A0A0A] border border-[#2A2A22] rounded-sm px-3 py-2 text-[12.5px] text-[#ECECE4] focus:outline-none focus:border-[#B8860B]" />
              </div>
            )}
            {channel === "mail" && (
              <div className="col-span-2">
                <label className="block text-[10px] uppercase tracking-[0.12em] text-[#8A8A8A] mb-1">Lender mailing address *</label>
                <textarea value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Attn: Payoffs Dept.&#10;123 Main St&#10;City, ST 00000" rows={3} data-testid={`submit-lender-address-${app.id}`}
                  className="w-full bg-[#0A0A0A] border border-[#2A2A22] rounded-sm px-3 py-2 text-[12.5px] text-[#ECECE4] focus:outline-none focus:border-[#B8860B]" />
              </div>
            )}
          </div>

          {channel === "mail" && (
            <div className="text-[10.5px] text-[#8A8A8A]">A USPS-ready print-and-mail packet is prepared with the address stamped on the cover. Print &amp; mail, then the desk can mark it sent.</div>
          )}

          {missing.length > 0 && (
            <div className="rounded-md border border-[#9E2A2B]/40 bg-[#9E2A2B]/10 px-3 py-2" data-testid={`submit-lender-server-missing-${app.id}`}>
              <div className="text-[11px] font-semibold text-[#FF8585]">Required by the desk:</div>
              <ul className="list-disc pl-5 mt-1 text-[11px] text-[#FFC9C9]">{missing.map((m, i) => <li key={i}>{m}</li>)}</ul>
            </div>
          )}

          {msg && <div className={`text-[11.5px] ${msg.ok ? "text-[#3DBB8E]" : "text-[#FF8585]"}`}>{msg.ok ? "✓ " : "⚠ "}{msg.t}</div>}

          <div className="flex items-center gap-2">
            <button onClick={submit} disabled={busy || !channelReady || localMissing.length > 0} data-testid={`submit-lender-send-${app.id}`}
              className="inline-flex items-center gap-1.5 bg-[#B8860B] hover:bg-[#a0760a] text-[#0A0A0A] px-4 py-2 text-[11px] uppercase tracking-[0.12em] font-bold rounded-sm disabled:opacity-40">
              {busy ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
              {channel === "efax" ? "eFax to Lender" : channel === "email" ? "Email to Lender" : "Prepare Mail Packet"}
            </button>
            <button onClick={previewPackage} data-testid={`submit-lender-preview-${app.id}`}
              className="inline-flex items-center gap-1.5 border border-[#2A2A22] hover:border-[#3A3A3A] text-[#9A9A90] px-3.5 py-2 text-[11px] uppercase tracking-[0.12em] font-bold rounded-sm">
              <FileText size={13} /> Preview package
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
